import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X,
  Download,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  AlertCircle,
  Loader2,
  FileQuestion,
  Maximize2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Converts a base64 data URL to a Blob object.
 */
function dataUrlToBlob(dataUrl) {
  try {
    const parts = dataUrl.split(',');
    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
    const byteString = atob(parts[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mime });
  } catch (err) {
    console.error('Failed to convert dataUrl to blob', err);
    return null;
  }
}

export default function AttachmentPreviewModal({ isOpen, onClose, attachment, title = 'Attachment View' }) {
  const [downloading, setDownloading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [previewError, setPreviewError] = useState(false);

  // Normalize attachment properties
  const isObject = attachment && typeof attachment === 'object';
  const name = isObject ? (attachment.name || attachment.filename || 'Receipt_Attachment') : (typeof attachment === 'string' ? attachment.split('?')[0].split('/').pop() || 'Attachment' : 'Attachment');
  const rawUrl = isObject ? (attachment.url || attachment.data_url || attachment.download_url || '') : (typeof attachment === 'string' ? attachment : '');
  const dataUrl = isObject && attachment.data_url ? attachment.data_url : (typeof rawUrl === 'string' && rawUrl.startsWith('data:') ? rawUrl : null);
  const isDataUrl = Boolean(dataUrl);
  const fileType = (isObject ? (attachment.type || attachment.content_type || '') : '').toLowerCase();

  const isDriveLink = typeof rawUrl === 'string' && (rawUrl.includes('drive.google.com') || rawUrl.includes('docs.google.com'));
  let driveEmbedUrl = null;
  if (isDriveLink) {
    if (rawUrl.includes('/file/d/')) {
      driveEmbedUrl = rawUrl.replace(/\/view(\?.*)?$/, '/preview').replace(/\/edit(\?.*)?$/, '/preview');
      if (!driveEmbedUrl.includes('/preview')) {
        driveEmbedUrl = driveEmbedUrl.replace(/\/$/, '') + '/preview';
      }
    } else {
      driveEmbedUrl = rawUrl;
    }
  }

  const isPdf = !isDriveLink && (fileType.includes('pdf') ||
    name.toLowerCase().endsWith('.pdf') ||
    (typeof rawUrl === 'string' && rawUrl.toLowerCase().includes('.pdf')) ||
    (dataUrl && dataUrl.startsWith('data:application/pdf')));

  const isImage = !isDriveLink && (fileType.startsWith('image/') ||
    /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(name) ||
    (dataUrl && dataUrl.startsWith('data:image/')) ||
    (typeof rawUrl === 'string' && /\.(jpg|jpeg|png|webp|gif|svg)/i.test(rawUrl)));

  // Build full accessible URL
  let fullUrl = isDriveLink ? (driveEmbedUrl || rawUrl) : rawUrl;
  if (fullUrl && fullUrl.startsWith('/api') && !fullUrl.startsWith('http')) {
    fullUrl = `${BACKEND_URL}${fullUrl}`;
  }

  // Handle blob URL generation for data URLs
  useEffect(() => {
    setPreviewError(false);
    if (!isOpen || !rawUrl) {
      setBlobUrl(null);
      return;
    }

    if (dataUrl) {
      const blob = dataUrlToBlob(dataUrl);
      if (blob) {
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        return () => {
          URL.revokeObjectURL(url);
        };
      }
    } else {
      setBlobUrl(null);
    }
  }, [isOpen, rawUrl, dataUrl]);

  if (!isOpen || !attachment) return null;

  const previewSource = blobUrl || fullUrl;

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const safeFilename = name.includes('.') ? name : `${name}${isPdf ? '.pdf' : isImage ? '.png' : ''}`;

      // 1. If base64 or blob URL is already in memory
      if (blobUrl || dataUrl) {
        const link = document.createElement('a');
        link.href = blobUrl || dataUrl;
        link.download = safeFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Downloaded: ${safeFilename}`);
        return;
      }

      // 2. If it is an API route or server path
      if (fullUrl && (fullUrl.includes('/api/') || fullUrl.startsWith('/'))) {
        const downloadEndpoint = isObject && attachment.download_url
          ? (attachment.download_url.startsWith('http') ? attachment.download_url : `${BACKEND_URL}${attachment.download_url}`)
          : (fullUrl.includes('?') ? `${fullUrl}&download=true` : `${fullUrl}?download=true`);

        const response = await axios.get(downloadEndpoint, {
          responseType: 'blob',
          withCredentials: true,
        });

        const dlBlob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/octet-stream',
        });
        const dlUrl = window.URL.createObjectURL(dlBlob);
        const link = document.createElement('a');
        link.href = dlUrl;
        link.download = safeFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(dlUrl);
        toast.success(`Downloaded: ${safeFilename}`);
        return;
      }

      // 3. Fallback for external links
      if (fullUrl) {
        window.open(fullUrl, '_blank', 'noopener,noreferrer');
        toast.info('Opened attachment in a new tab.');
      } else {
        toast.error('Unable to download attachment: source URL is missing.');
      }
    } catch (err) {
      console.error('Download failed', err);
      toast.error('Failed to download attachment. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-5 py-3.5 bg-slate-900 text-white flex items-center justify-between border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0 pr-2">
            <div className="p-1.5 bg-blue-500/20 text-blue-400 rounded-lg border border-blue-500/30 shrink-0">
              {isPdf ? <FileText className="w-5 h-5 text-rose-400" /> : isImage ? <ImageIcon className="w-5 h-5 text-emerald-400" /> : <FileQuestion className="w-5 h-5" />}
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-sm truncate text-slate-100" title={name}>
                {name}
              </h3>
              <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono">
                <span>{isDriveLink ? 'Google Drive Document' : isPdf ? 'PDF Document' : isImage ? 'Image Receipt' : 'Attached File'}</span>
                {isObject && attachment.size_formatted && (
                  <>
                    <span>•</span>
                    <span>{attachment.size_formatted}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 shadow-sm"
              title="Download file"
            >
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              <span>Download</span>
            </button>

            {fullUrl && !isDataUrl && (
              <a
                href={fullUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors"
                title="Open in new window"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}

            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors ml-1"
              title="Close preview"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Content / Preview Canvas */}
        <div className="flex-1 overflow-auto p-4 bg-slate-100/70 flex items-center justify-center min-h-[350px] max-h-[75vh]">
          {previewError ? (
            <div className="text-center p-8 bg-white rounded-2xl border border-slate-200 max-w-md shadow-sm space-y-3">
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto" />
              <h4 className="font-bold text-slate-800 text-sm">Preview Unavailable</h4>
              <p className="text-xs text-slate-600">
                This document cannot be previewed inline, or requires direct downloading to view.
              </p>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-xl shadow-sm inline-flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> Download File Now
              </button>
            </div>
          ) : (isPdf || isDriveLink) && previewSource ? (
            <div className="w-full h-full min-h-[500px] flex flex-col bg-white rounded-xl shadow-inner border border-slate-200 overflow-hidden">
              <iframe
                src={previewSource}
                title={name}
                className="w-full flex-1 min-h-[500px] border-0"
                allow="autoplay"
                onError={() => setPreviewError(true)}
              />
            </div>
          ) : isImage && previewSource ? (
            <div className="flex items-center justify-center w-full h-full">
              <img
                src={previewSource}
                alt={name}
                className="max-h-[68vh] max-w-full object-contain rounded-xl shadow-md border border-slate-200 bg-white p-2"
                onError={() => setPreviewError(true)}
              />
            </div>
          ) : (
            <div className="text-center p-8 bg-white rounded-2xl border border-slate-200 max-w-md shadow-sm space-y-3">
              <FileQuestion className="w-12 h-12 text-blue-500 mx-auto" />
              <h4 className="font-bold text-slate-800 text-sm">{name}</h4>
              <p className="text-xs text-slate-600">
                This file format does not support embedded browser preview. You can safely download and open it on your device.
              </p>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-xl shadow-sm inline-flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> Download Attachment
              </button>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-5 py-2.5 bg-white border-t border-slate-200 flex items-center justify-between text-xs text-slate-500 shrink-0">
          <span className="truncate">
            {isObject && attachment.file_id ? `File ID: ${attachment.file_id}` : name}
          </span>
          <button
            onClick={onClose}
            className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
